import React, { useEffect, useState } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import ContactForm from '../components/ContactForm'
import './CreatorProfile.css'

export default function CreatorProfile() {
  const { username } = useParams()
  const { user } = useAuth()
  const location = useLocation()
  const [creator, setCreator] = useState(null)
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [showContact, setShowContact] = useState(false)
  const [forSaleOnly, setForSaleOnly] = useState(false)
  const [showSupport, setShowSupport] = useState(false)
  const [supportAmount, setSupportAmount] = useState(5)
  const [supportFreq, setSupportFreq] = useState('one_time')
  const [supportMessage, setSupportMessage] = useState('')
  const [supportSaving, setSupportSaving] = useState(false)
  const [supportDone, setSupportDone] = useState(false)

  useEffect(() => { fetchProfile() }, [username, location.key]) // eslint-disable-line

  async function fetchProfile() {
    setLoading(true)
    const { data: prof } = await supabase.from('profiles').select('*').eq('username', username).single()
    if (!prof) { setNotFound(true); setLoading(false); return }
    setCreator(prof)
    const { data: projs } = await supabase.from('projects').select('*')
      .eq('user_id', prof.id).eq('visibility', 'public')
      .neq('status', 'rejected').order('created_at', { ascending: false })
    setProjects(projs || [])
    setLoading(false)
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><span className="loading-spinner" /></div>
  if (notFound) return (
    <div className="container" style={{ textAlign: 'center', padding: 80 }}>
      <h2>Creator not found</h2>
      <p style={{ color: 'var(--text-2)', marginTop: 8 }}>No creator with that username exists.</p>
      <Link to="/browse" className="btn btn-secondary" style={{ marginTop: 24 }}>Browse verified work</Link>
    </div>
  )

  const isOwner = user && user.id === creator.id
  const displayName = creator.display_name || creator.full_name || username
  const verified = projects.filter(p => p.status === 'verified' && (!p.admin_hidden || isOwner))
  const verifiedShown = forSaleOnly ? verified.filter(p => p.for_sale) : verified
  const ongoing = projects.filter(p => p.status === 'ongoing')
  const canContact = verified.length > 0

  async function submitSupport() {
    if (!user) { window.location.assign('/login'); return }
    if (!supportAmount || supportAmount <= 0) return
    setSupportSaving(true)
    const { error } = await supabase.from('support_pledges').insert({
      creator_id: creator.id,
      supporter_id: user.id,
      amount: supportAmount,
      frequency: supportFreq,
      message: supportMessage || null,
    })
    setSupportSaving(false)
    if (error) { alert(error.message); return }
    setSupportDone(true)
  }

  return (
    <div className="profile-page page-fade">
      <div className="container">

        {/* Banner */}
        <div className="profile-banner">
          {creator.banner_url
            ? <img src={creator.banner_url} alt="Profile banner" className="profile-banner-img" />
            : <div className="profile-banner-empty" />
          }
        </div>

        {/* Profile header */}
        <div className="profile-header card">
          <div className="profile-header-left">
            <div className="profile-avatar-wrap">
              {creator.avatar_url
                ? <img src={creator.avatar_url} alt={displayName} className="profile-avatar-img" />
                : <div className="profile-avatar">{displayName.charAt(0).toUpperCase()}</div>
              }
            </div>
            <div>
              <div className="profile-name-row">
                <h1>{displayName}</h1>
              </div>
              <div className="profile-username">@{username} · humanorigin.app/u/{username}</div>
              {creator.bio && <p className="profile-bio">{creator.bio}</p>}
              {creator.website && (
                <a href={creator.website} target="_blank" rel="noopener noreferrer" className="profile-website">
                  🌐 {creator.website.replace(/^https?:\/\//, '')}
                </a>
              )}
              <div className="profile-stats">
                <div className="profile-stat"><span>{verified.length}</span> verified</div>
                <div className="profile-stat"><span>{ongoing.length}</span> in progress</div>
                <div className="profile-stat"><span>{projects.length}</span> total works</div>
              </div>
            </div>
          </div>
          <div className="profile-header-actions">
            {isOwner && (
              <Link to="/settings/profile" className="btn btn-secondary btn-sm">Edit profile →</Link>
            )}
            {!isOwner && canContact && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowContact(!showContact)}>
                {showContact ? 'Cancel' : 'Contact creator →'}
              </button>
            )}
            {!isOwner && !canContact && (
              <span className="coming-soon-flag">Contact · after first verification</span>
            )}
            {!isOwner && (
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowSupport(v => !v); setSupportDone(false) }}>
                {showSupport ? 'Cancel' : '☕ Help this human'}
              </button>
            )}
          </div>
        </div>

        {showContact && !isOwner && (
          <div className="card" style={{ marginTop: 16, marginBottom: 8 }}>
            <ContactForm creatorId={creator.id} creatorName={displayName} />
          </div>
        )}

        {showSupport && !isOwner && (
          <div className="card" style={{ marginTop: 16, marginBottom: 8 }}>
            {supportDone ? (
              <div className="success-msg" style={{ textAlign: 'center', padding: 12 }}>
                Thanks for supporting {displayName}! (Demo mode — payment processing isn't connected yet, so nothing was charged.)
              </div>
            ) : (
              <>
                <h3 style={{ marginBottom: 4, fontSize: 16 }}>Help {displayName} keep making things</h3>
                <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16 }}>
                  A no-strings way to support human creators directly — like Buy Me a Coffee or Patreon.
                </p>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <button className={`btn btn-sm ${supportFreq === 'one_time' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSupportFreq('one_time')}>One-time</button>
                  <button className={`btn btn-sm ${supportFreq === 'monthly' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSupportFreq('monthly')}>Monthly</button>
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  {[3, 5, 10, 25].map(amt => (
                    <button key={amt} className={`btn btn-sm ${supportAmount === amt ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSupportAmount(amt)}>${amt}</button>
                  ))}
                  <input
                    type="number" min="1" placeholder="Custom"
                    value={[3, 5, 10, 25].includes(supportAmount) ? '' : supportAmount}
                    onChange={e => setSupportAmount(parseFloat(e.target.value) || 0)}
                    style={{ width: 90 }}
                  />
                </div>
                <div className="form-group">
                  <label>Message (optional)</label>
                  <textarea rows={2} placeholder="Love your work!" value={supportMessage} onChange={e => setSupportMessage(e.target.value)} />
                </div>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={submitSupport} disabled={supportSaving}>
                  {supportSaving ? <span className="loading-spinner" /> : `Support $${supportAmount}${supportFreq === 'monthly' ? '/mo' : ''} →`}
                </button>
                <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 8, textAlign: 'center' }}>
                  Proof of concept — no payment gateway connected yet.
                </p>
              </>
            )}
          </div>
        )}

        {verified.length > 0 && (
          <div className="profile-trust-bar">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <circle cx="7.5" cy="7.5" r="6.5" stroke="#c8f55a" strokeWidth="1.2"/>
              <path d="M4.5 7.5l2 2 4-4" stroke="#c8f55a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            All verified work on this profile has been reviewed and confirmed by a human member of the HumanOrigin team.
          </div>
        )}

        {projects.length === 0 ? (
          <div className="profile-empty">
            <div style={{ fontSize: 40, marginBottom: 16 }}>🎨</div>
            <h3>No public work yet</h3>
            {isOwner && <p>Start a project and document your creative process.</p>}
            {isOwner && <Link to="/project/new" className="btn btn-primary" style={{ marginTop: 20 }}>Start a project →</Link>}
          </div>
        ) : (
          <>
            {ongoing.length > 0 && (
              <div className="profile-section">
                <h2>Human at work <span className="profile-section-count">{ongoing.length}</span></h2>
                <div className="profile-projects-grid">
                  {ongoing.map(p => <ProjectCard key={p.id} project={p} />)}
                </div>
              </div>
            )}
            {verified.length > 0 && (
              <div className="profile-section">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <h2>Verified work <span className="profile-section-count">{verified.length}</span></h2>
                  {verified.some(p => p.for_sale) && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={forSaleOnly} onChange={e => setForSaleOnly(e.target.checked)} />
                      Available for sale only
                    </label>
                  )}
                </div>
                {verifiedShown.length === 0 ? (
                  <p style={{ color: 'var(--text-2)', fontSize: 14, marginTop: 12 }}>Nothing currently for sale.</p>
                ) : (
                  <div className="profile-projects-grid">
                    {verifiedShown.map(p => <ProjectCard key={p.id} project={p} />)}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function ProjectCard({ project }) {
  const isVerified = project.status === 'verified'
  const isOngoing = project.status === 'ongoing'
  const isSubmitted = project.status === 'submitted'
  const isNeedsEvidence = project.status === 'needs_evidence'

  const statusLabel = isOngoing
    ? 'Ongoing'
    : isSubmitted
    ? 'In Review'
    : isNeedsEvidence
    ? 'Needs Evidence'
    : null

  return (
    <Link to={`/project/${project.id}`} className="ppc-card">
      <div className="ppc-banner">
        {isVerified && project.banner_url ? (
          <img src={project.banner_url} alt={project.title} className="ppc-banner-img" />
        ) : (
          <div className="ppc-banner-placeholder">
            <span className="ppc-banner-icon">
              {isVerified ? project.title.charAt(0) : '✦'}
            </span>
            {statusLabel && (
              <span className="ppc-banner-status-label">{statusLabel}</span>
            )}
          </div>
        )}
        <div className="ppc-banner-badges">
          <span className="ppc-cat">
            {project.custom_category ? `Other / ${project.custom_category}` : project.category}
          </span>
          {isVerified && (
            <span className="ppc-verified-dot" title="Verified">✓</span>
          )}
        </div>
        {isVerified && project.for_sale && (
          <span className="browse-card-price-badge">${project.price}</span>
        )}
      </div>
      <div className="ppc-body">
        <h3 className="ppc-title">{project.title}</h3>
        {project.description && (
          <p className="ppc-desc">{project.description.slice(0, 90)}{project.description.length > 90 ? '…' : ''}</p>
        )}
      </div>
      <div className="ppc-footer">
        <span className="ppc-date">
          {new Date(project.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
        {isVerified && <span className="ppc-verified-label">✓ Verified</span>}
      </div>
    </Link>
  )
}
